import React, {FunctionComponent, useContext, useEffect, useState} from 'react';
import MicrositePageContainer from '../../components/page/MicrositePageContainer';
import {LogoSize} from '../../components/page/PageHeader';
import MicrositeContext from '../../contexts/microsite-context';
import AppContext from '../../contexts/app-context';
import {useNavigate} from 'react-router-dom';
import Button, {ButtonType} from '../../components/common/Button';
import ButtonSet from '../../components/common/ButtonSet';
import Padded from '../../components/common/Padded';
import Module, {ModuleHeader} from '../../components/common/Module';
import ListType from '../../types/list-type';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

type SortMode = 'alpha' | 'manual';

type OrderItem = {
  id: number
  name: string
  pinned: boolean
  sortOrder: number | null
};

type SortableItemProps = {
  item: OrderItem
  isManual: boolean
  onTogglePin: (id: number) => void
};

const SortableItem: FunctionComponent<SortableItemProps> = ({item, isManual, onTogglePin}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: item.id,
    disabled: !isManual,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isManual ? 'grab' : 'default',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Module>
        <ModuleHeader
          title={
            <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <button
                type="button"
                onClick={() => onTogglePin(item.id)}
                title={item.pinned ? 'Unpin' : 'Pin'}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 2px',
                  fontSize: '16px',
                  lineHeight: 1,
                  color: item.pinned ? '#333' : '#aaa',
                }}
              >
                {item.pinned ? '📌' : '📍'}
              </button>
              {item.name}
            </span>
          }
          right={
            isManual ? (
              <span
                {...attributes}
                {...listeners}
                title="Drag to reorder"
                style={{cursor: 'grab', fontSize: '18px', padding: '0 4px', userSelect: 'none'}}
              >
                ⠿
              </span>
            ) : undefined
          }
        />
      </Module>
    </div>
  );
};

const SetOrderPage: FunctionComponent = () => {
  const {authUser} = useContext(AppContext);
  const {microsite, service} = useContext(MicrositeContext);
  const nav = useNavigate();

  const [pinnedSortMode, setPinnedSortMode] = useState<SortMode>(
    microsite.config.pinnedSortMode ?? 'alpha'
  );
  const [unpinnedSortMode, setUnpinnedSortMode] = useState<SortMode>(
    microsite.config.unpinnedSortMode ?? 'alpha'
  );
  const [pinnedItems, setPinnedItems] = useState<OrderItem[]>([]);
  const [unpinnedItems, setUnpinnedItems] = useState<OrderItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authUser === null) nav('/');
  }, [authUser, nav]);

  useEffect(() => {
    const pinned: OrderItem[] = [];
    const unpinned: OrderItem[] = [];
    microsite.lists.forEach(list => {
      const item: OrderItem = {id: list.id, name: list.name, pinned: list.pinned, sortOrder: list.sortOrder};
      if (list.pinned) pinned.push(item);
      else unpinned.push(item);
    });
    setPinnedItems(pinned);
    setUnpinnedItems(unpinned);
  }, [microsite.lists]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const handleDragEnd = (setter: React.Dispatch<React.SetStateAction<OrderItem[]>>) =>
    (event: DragEndEvent) => {
      const {active, over} = event;
      if (over && active.id !== over.id) {
        setter(items => {
          const oldIndex = items.findIndex(i => i.id === active.id);
          const newIndex = items.findIndex(i => i.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    };

  const togglePin = (id: number) => {
    const fromPinned = pinnedItems.find(i => i.id === id);
    if (fromPinned) {
      setPinnedItems(prev => prev.filter(i => i.id !== id));
      setUnpinnedItems(prev => {
        const updated = [...prev, {...fromPinned, pinned: false}];
        if (unpinnedSortMode === 'alpha') {
          updated.sort((a, b) => a.name.localeCompare(b.name));
        }
        return updated;
      });
    } else {
      const fromUnpinned = unpinnedItems.find(i => i.id === id);
      if (!fromUnpinned) return;
      setUnpinnedItems(prev => prev.filter(i => i.id !== id));
      setPinnedItems(prev => {
        const updated = [...prev, {...fromUnpinned, pinned: true}];
        if (pinnedSortMode === 'alpha') {
          updated.sort((a, b) => a.name.localeCompare(b.name));
        }
        return updated;
      });
    }
  };

  const handleSortModeChange = (section: 'pinned' | 'unpinned', mode: SortMode) => {
    if (section === 'pinned') {
      setPinnedSortMode(mode);
      if (mode === 'alpha') {
        setPinnedItems(prev => [...prev].sort((a, b) => a.name.localeCompare(b.name)));
      }
    } else {
      setUnpinnedSortMode(mode);
      if (mode === 'alpha') {
        setUnpinnedItems(prev => [...prev].sort((a, b) => a.name.localeCompare(b.name)));
      }
    }
  };

  const buildLists = () => {
    const allItems: Array<{id: number, pinned: boolean, sortOrder: number | null}> = [];
    pinnedItems.forEach((item, ix) => {
      allItems.push({id: item.id, pinned: true, sortOrder: pinnedSortMode === 'manual' ? ix : null});
    });
    unpinnedItems.forEach((item, ix) => {
      allItems.push({id: item.id, pinned: false, sortOrder: unpinnedSortMode === 'manual' ? ix : null});
    });
    return allItems;
  };

  const handleSave = () => {
    if (!authUser) return;
    setIsSaving(true);
    setError(null);
    service.saveListOrder(authUser, microsite.slug, {
      pinnedSortMode,
      unpinnedSortMode,
      lists: buildLists(),
    }).then(() => {
      nav('/manage');
    }).catch(() => {
      setError('Failed to save order');
      setIsSaving(false);
    });
  };

  const SortModeToggle: FunctionComponent<{mode: SortMode, onChange: (m: SortMode) => void}> = ({mode, onChange}) => (
    <ButtonSet>
      <Button type={mode === 'alpha' ? ButtonType.Primary : ButtonType.Secondary} onClick={() => onChange('alpha')}>
        A–Z
      </Button>
      <Button type={mode === 'manual' ? ButtonType.Primary : ButtonType.Secondary} onClick={() => onChange('manual')}>
        Manual
      </Button>
    </ButtonSet>
  );

  return (
    <MicrositePageContainer logoSize={LogoSize.Small} padded={false}>
      <Padded>
        <div className="txt-center m-t">
          <ButtonSet>
            <Button type={ButtonType.Secondary} onClick={() => nav('/manage')}>
              Back to Manage
            </Button>
            <Button type={ButtonType.Primary} disabled={isSaving} onClick={handleSave}>
              {isSaving ? 'Saving…' : 'Save Order'}
            </Button>
          </ButtonSet>
          {error && <div className="m-t-sm" style={{color: 'red'}}>{error}</div>}
        </div>
      </Padded>

      <div style={{backgroundColor: '#f0f0f0'}}>
        <Padded>

          {/* Pinned section */}
          <div className="m-b">
            <div className="flex m-b" style={{alignItems: 'center'}}>
              <div className="flex-grow align-center">
                <h2 style={{margin: 0}}>Pinned</h2>
              </div>
              <SortModeToggle mode={pinnedSortMode} onChange={m => handleSortModeChange('pinned', m)} />
            </div>
            {pinnedItems.length === 0 ? (
              <p style={{color: '#888'}}>No pinned items. Click 📍 on an item below to pin it.</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd(setPinnedItems)}>
                <SortableContext items={pinnedItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                  {pinnedItems.map(item => (
                    <SortableItem key={item.id} item={item} isManual={pinnedSortMode === 'manual'} onTogglePin={togglePin} />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Unpinned section */}
          <div>
            <div className="flex m-b" style={{alignItems: 'center'}}>
              <div className="flex-grow align-center">
                <h2 style={{margin: 0}}>Unpinned</h2>
              </div>
              <SortModeToggle mode={unpinnedSortMode} onChange={m => handleSortModeChange('unpinned', m)} />
            </div>
            {unpinnedItems.length === 0 ? (
              <p style={{color: '#888'}}>No unpinned items.</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd(setUnpinnedItems)}>
                <SortableContext items={unpinnedItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                  {unpinnedItems.map(item => (
                    <SortableItem key={item.id} item={item} isManual={unpinnedSortMode === 'manual'} onTogglePin={togglePin} />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>

        </Padded>
      </div>
    </MicrositePageContainer>
  );
};

export default SetOrderPage;
